import { useState, useCallback, useEffect, useRef } from "react";
import { RecordingEntry } from "@/modules/common/types/audio.types";
import useAudioRecorder from "@/modules/common/hooks/audio/useAudioRecorder";
import useAudioPlayback from "@/modules/common/hooks/audio/useAudioPlayback";
import { WAVEFORM_BAR_COUNT } from "@/modules/common/utils/audio.utils";

interface UseRecordingManagerOptions {
  maxRecordings?: number;
  onRecordingsChange?: (recordings: RecordingEntry[]) => void;
  maxDuration?: number;
  onMaxDurationReached?: () => void;
}

interface UseRecordingManagerReturn {
  recordings: RecordingEntry[];
  isRecording: boolean;
  isPaused: boolean;
  isInitializing: boolean;
  recordingTime: number;
  audioLevels: number[];
  canAddMoreRecordings: boolean;
  permissionError: string | null;
  startRecording: () => void;
  cancelRecording: () => void;
  confirmRecording: () => void;
  pauseResume: () => void;
  removeRecording: (id: string) => void;
  togglePlayback: (recording: RecordingEntry) => void;
  isPlaying: (id: string) => boolean;
  getCurrentTimeForId: (id: string) => number;
  getDurationForId: (id: string) => number;
  getErrorForId: (id: string) => string | null;
  seekTo: (id: string, time: number) => void;
  playbackRate: number;
  setPlaybackRate: (rate: number) => void;
  clearPermissionError: () => void;
}

const useRecordingManager = ({
  maxRecordings = 4,
  onRecordingsChange,
  maxDuration,
  onMaxDurationReached,
}: UseRecordingManagerOptions = {}): UseRecordingManagerReturn => {
  const [recordings, setRecordings] = useState<RecordingEntry[]>([]);

  const playback = useAudioPlayback();

  const handleRecordingComplete = useCallback(
    async (audioBlob: Blob, duration: number, waveformData: number[]) => {
      const recordingId = `recording-${Date.now()}`;

      const newEntry: RecordingEntry = {
        id: recordingId,
        audioBlob,
        duration,
        waveformData,
      };

      setRecordings((prev) => [...prev, newEntry]);
    },
    [],
  );

  const recorder = useAudioRecorder({
    onRecordingComplete: handleRecordingComplete,
    waveformBarCount: WAVEFORM_BAR_COUNT,
    maxDuration,
    onMaxDurationReached,
  });

  useEffect(() => {
    onRecordingsChange?.(recordings);
  }, [recordings, onRecordingsChange]);

  const stopRecordingRef = useRef(recorder.stopRecording);
  const playbackCleanupRef = useRef(playback.cleanup);

  useEffect(() => {
    stopRecordingRef.current = recorder.stopRecording;
  }, [recorder.stopRecording]);

  useEffect(() => {
    playbackCleanupRef.current = playback.cleanup;
  }, [playback.cleanup]);

  useEffect(() => {
    return () => {
      stopRecordingRef.current();
      playbackCleanupRef.current();
    };
  }, []);

  const startRecording = useCallback(() => {
    recorder.startRecording();
  }, [recorder]);

  const cancelRecording = useCallback(() => {
    recorder.cancelRecording();
  }, [recorder]);

  const pauseResume = useCallback(() => {
    if (recorder.isPaused) {
      recorder.resumeRecording();
    } else {
      recorder.pauseRecording();
    }
  }, [recorder]);

  const removeRecording = useCallback(
    (id: string) => {
      setRecordings((prev) => prev.filter((r) => r.id !== id));
      if (playback.isPlaying(id) || playback.isLoaded(id)) {
        playback.stopPlayback();
      }
      // Clear any error associated with this recording
      playback.clearErrorForId(id);
    },
    [playback],
  );

  const togglePlayback = useCallback(
    (recording: RecordingEntry) => {
      // Prioritize audioUrl (backend URL) if available, otherwise use audioBlob (local recording)
      const source = recording.audioUrl || recording.audioBlob;
      
      if (source) {
        playback.togglePlay(recording.id, source);
      }
    },
    [playback],
  );

  return {
    recordings,
    isRecording: recorder.isRecording,
    isPaused: recorder.isPaused,
    isInitializing: recorder.isInitializing,
    recordingTime: recorder.recordingTime,
    audioLevels: recorder.audioLevels,
    canAddMoreRecordings: recordings.length < maxRecordings,
    permissionError: recorder.permissionError,
    startRecording,
    cancelRecording,
    confirmRecording: recorder.confirmRecording,
    pauseResume,
    removeRecording,
    togglePlayback,
    isPlaying: playback.isPlaying,
    getCurrentTimeForId: playback.getCurrentTimeForId,
    getDurationForId: playback.getDurationForId,
    getErrorForId: playback.getErrorForId,
    seekTo: playback.seekTo,
    playbackRate: playback.playbackRate,
    setPlaybackRate: playback.setPlaybackRate,
    clearPermissionError: recorder.clearPermissionError,
  };
};

export default useRecordingManager;
